import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from 'shared/hooks/useApi.ts';
import io from 'socket.io-client';
import {
  RealtimerProviderContext,
  RealtimerProviderContextValue,
} from './RealtimerProviderContext.ts';

const connection = io(import.meta.env.VITE_REALTIMER_URL as string, {
  autoConnect: false,
  extraHeaders: {
    'target-service': 'realtimer',
  },
});

export const RealtimerProvider = ({ children }: PropsWithChildren) => {
  const [isConnected, setIsConnected] = useState(false);

  const subscribe = useCallback((audience: string, callback: (message: any) => any) => {
    if (!connection) {
      return;
    }

    connection.on(audience, callback);
    connection.emit('subscribe', { audience });
  }, []);

  const unsubscribe = useCallback((audience: string, callback: (message: any) => any) => {
    if (!connection) {
      return;
    }

    connection.off(audience, callback);
    connection.emit('unsubscribe', { audience });
  }, []);

  const { data, refetch: refetchAccessToken } = useApi<{ token: string }>('realtimer-access-token');

  useEffect(() => {
    if (!connection) {
      return;
    }

    connection.on('connect', () => setIsConnected(true));
    connection.on('disconnect', () => setIsConnected(false));

    if (connection.connected) {
      return;
    }

    const token = data?.token;
    if (!token) {
      return;
    }

    connection.auth = { token };
    connection.connect();
  }, [data?.token, setIsConnected]);

  useEffect(() => {
    if (isConnected) {
      return;
    }

    refetchAccessToken();
  }, [isConnected, refetchAccessToken]);

  const value: RealtimerProviderContextValue = useMemo(
    () => ({
      subscribe,
      unsubscribe,
      isConnected,
    }),
    [subscribe, unsubscribe, isConnected],
  );

  return (
    <RealtimerProviderContext.Provider value={value}>{children}</RealtimerProviderContext.Provider>
  );
};
